/* eslint-disable class-methods-use-this, no-undef */
/**
 * @note must enable no-undef eslint rule
 * had to turn it off cause lint is throwing
 *    'NodeListOf' is not defined no-undef
 * fix: need to set the TS target compiler option
 * to es6 or higher for NodeListOf
 */
import { phone } from 'phone';
import { trackButtonPress, trackEvent } from '../utils/analytics';

const doc = document;

export class TailwindPageBehavior {
  protected cached: {
    navMenu: HTMLElement;
    navMenuItemDownloadApp: HTMLAnchorElement;
    heroFormContainer: HTMLDivElement;
    heroForm: HTMLFormElement;
    heroFormPhoneInput: HTMLInputElement;
    heroFormError: HTMLParagraphElement;
    heroFormSuccessContainer: HTMLDivElement;
    heroSubmit: HTMLButtonElement;
    heroFormFields: {
      firstName: HTMLInputElement;
      lastName: HTMLInputElement;
      email: HTMLInputElement;
      phoneInput: HTMLInputElement;
      zipCode: HTMLInputElement;
      referer: HTMLInputElement;
      referralToggle: HTMLDivElement;
      referralField: HTMLDivElement;
      legalToggle: HTMLDivElement;
      moreLegal: HTMLParagraphElement;
    };
    carousel: {
      prev: HTMLImageElement;
      next: HTMLImageElement;
      roots: NodeListOf<HTMLDivElement>;
      firstItems: NodeListOf<HTMLDivElement>;
    };
    faqItems: NodeListOf<HTMLDivElement>;
    textMeForms: NodeListOf<HTMLFormElement>;
    textMeFormInputs: NodeListOf<HTMLInputElement>;
    textMeFormSubmitBtns: NodeListOf<HTMLButtonElement>;
  };
  protected gon: Record<string, any>;

  constructor() {
    this.cached = {
      navMenu: doc.querySelector('#main-menu') as HTMLElement,
      navMenuItemDownloadApp: doc.querySelector(
        '.download-app'
      ) as HTMLAnchorElement,
      heroFormContainer: doc.querySelector('#form-container'),
      heroForm: doc.querySelector('#hero-form'),
      heroFormPhoneInput: doc.querySelector('#hero-phone'),
      heroFormError: doc.querySelector('#form-error'),
      heroFormSuccessContainer: doc.querySelector('#success-container'),
      heroSubmit: doc.querySelector('#hero-submit'),
      heroFormFields: {
        firstName: doc.querySelector('#firstName'),
        lastName: doc.querySelector('#lastName'),
        email: doc.querySelector('#email'),
        phoneInput: doc.querySelector('#hero-phone'),
        zipCode: doc.querySelector('#zip'),
        referer: doc.querySelector('#referralCode'),
        referralToggle: doc.querySelector('.referral-toggle'),
        referralField: doc.querySelector('.referral-field'),
        legalToggle: doc.querySelector('.legal-toggle'),
        moreLegal: doc.querySelector('.more-legal'),
      },
      carousel: {
        prev: doc.querySelector('#prev-arrow'),
        next: doc.querySelector('#next-arrow'),
        roots: doc.querySelectorAll('.carousel'),
        firstItems: doc.querySelectorAll('.carousel-first-item'),
      },
      faqItems: doc.querySelectorAll('.faq-item'),
      textMeForms: doc.querySelectorAll('.text-me-form'),
      textMeFormInputs: doc.querySelectorAll('.text-me-input'),
      textMeFormSubmitBtns: doc.querySelectorAll('.text-me-submit'),
    };
    this.gon = (window as any).gon;
  }

  /**
   * add Nav Menu behavior
   */
  public navMenuBehavior() {
    const { navMenu } = this.cached;
    navMenu.addEventListener('click', (event) => {
      if (event.target instanceof HTMLAnchorElement) {
        trackButtonPress({
          source_type: 'navigation',
          source: event.target.dataset.source,
          step_value: 'zipcode',
        });
      }
    });
  }

  /**
   * add Hero form behavior
   */
  public heroFormBehavior() {
    const {
      heroForm,
      heroFormPhoneInput,
      heroSubmit,
      heroFormFields: { referralToggle, referralField, legalToggle, moreLegal },
    } = this.cached;

    heroForm.addEventListener(
      'blur',
      (event) => {
        if (event.target instanceof HTMLInputElement) {
          const element = event.target;
          if (element.classList.contains('required')) {
            if (element.value) {
              element.nextElementSibling.classList.add('hidden');
              element.classList.remove('border-red-500');
            } else {
              element.nextElementSibling.classList.remove('hidden');
              element.classList.add('border-red-500');
            }
          }
        }
      },
      true
    );

    heroFormPhoneInput.addEventListener('change', (event) => {
      const element = event.target as HTMLInputElement;
      const phoneVal = phone(element.value);
      if (phoneVal.isValid) {
        element.classList.remove('border-red-500');
        element.nextElementSibling.nextElementSibling.classList.add('hidden');
      } else {
        element.classList.add('border-red-500');
        element.nextElementSibling.nextElementSibling.classList.remove(
          'hidden'
        );
      }
    });

    referralToggle.addEventListener('click', () => {
      referralToggle.classList.toggle('hidden');
      referralField.classList.toggle('hidden');
    });

    legalToggle.addEventListener('click', () => {
      legalToggle.classList.toggle('hidden');
      moreLegal.classList.toggle('hidden');
    });

    heroForm.addEventListener('submit', (event) => {
      event.preventDefault();

      let heroSubmitContent: string | null = null;
      const onSubmit = () => {
        heroSubmitContent = heroSubmit.textContent;
        heroSubmit.textContent = heroSubmit.getAttribute(
          'data-submitting-text'
        );
        heroSubmit.setAttribute('disabled', '');
      };

      const resetSubmit = () => {
        heroSubmit.textContent = heroSubmitContent;
        heroSubmit.removeAttribute('disabled')
      };

      onSubmit();

      fetch('/initial_signup', {
        method: 'POST',
        headers: {
          'data-type': 'json',
          'Content-Type': 'application/json',
          'x-csrf-token': (doc.querySelector('meta[name="csrf-token"]') as any)
            .content,
        },
        body: this.formPayload,
      })
        .then(async (response) => {
          const parsedData = await response.json();
          if (response.ok) {
            this.heroFormGenericSuccess(parsedData);
          } else if (response.status === 422) {
            for (const [columnName, messages] of Object.entries(
              parsedData || {}
            )) {
              this.heroFormSpecificError(columnName, messages as string[]);
            }
          } else {
            resetSubmit();
            throw new Error(response.statusText);
          }
          resetSubmit();
        })
        .catch((error) => {
          resetSubmit();
          this.heroFormGenericError(error.message);
        });
    });
  }

  protected get formPayload() {
    const {
      heroFormFields: {
        firstName,
        lastName,
        email,
        phoneInput,
        zipCode,
        referer,
      },
    } = this.cached;
    return JSON.stringify({
      applicant: {
        first_name: firstName ? firstName.value : null,
        last_name: lastName ? lastName.value : null,
        email: email ? email.value : null,
        phone: phoneInput ? phoneInput.value : null,
        zip_code: zipCode ? zipCode.value : null,
        referer: referer ? referer.value : null,
      },
      city: this.gon.currentCity,
    });
  }

  protected heroFormGenericError(message: string) {
    const { heroFormError } = this.cached;

    heroFormError.textContent = message;
    heroFormError.classList.remove('hidden');
  }

  protected heroFormSpecificError(columnName: string, messages: string[]) {
    const selector: Record<string, string> = {
      email: '#hero-email-container .required-message',
      phone: '#hero-phone-container .required-message',
      zip_code: '#hero-zip-code-container .required-message',
    };
    const element = selector[columnName]
      ? doc.querySelector(selector[columnName])
      : doc.querySelector('#form-error');

    element.textContent = messages.join(';');
    element.classList.remove('hidden');
  }

  protected heroFormGenericSuccess(data: any) {
    const { heroFormContainer, heroFormSuccessContainer } = this.cached;

    heroFormContainer.remove();
    heroFormSuccessContainer.classList.remove('hidden');

    this.trackSuccessEvent(data, this.formPayload);
  }

  protected trackSuccessEvent(
    responseData: Record<string, any>,
    formPayloadStringified: string
  ) {
    if (responseData && responseData.spuid) {
      const event_payload = {
        source_type: 'signup_form_submit',
        source: formPayloadStringified,
        step_value: 'signup',
        spuid: responseData.spuid,
      };
      if ('email_digest' in responseData) {
        event_payload.email_digest = responseData.email_digest;
      }
      trackEvent('signup', event_payload);
    }
  }

  /**
   * add Carousel behavior
   */
  public carouselBehavior() {
    /**
     * @note currently using the margin-left style property
     * of the first child to create carousel sliding behavior,
     * experience can be improved by using transform/translate.
     */
    const {
      carousel: { prev, next, roots, firstItems },
    } = this.cached;
    if (roots.length === 0) {
      return;
    }
    let currentSlide = 0;
    const totalSlides = roots[0].querySelectorAll('.carousel-item').length;

    const updateCarouselBtns = () => {
      if (currentSlide > 0 && totalSlides > 1) {
        prev.classList.remove('opacity-25');
        prev.classList.add('cursor-pointer');
      } else {
        prev.classList.add('opacity-25');
        prev.classList.remove('cursor-pointer');
      }

      if (currentSlide + 1 < totalSlides && totalSlides > 1) {
        next.classList.remove('opacity-25');
        next.classList.add('cursor-pointer');
      } else {
        next.classList.add('opacity-25');
        next.classList.remove('cursor-pointer');
      }
    };

    prev.addEventListener('click', () => {
      if (currentSlide > 0) {
        const prevSlide = currentSlide - 1;
        firstItems.forEach((firstItem) => {
          firstItem.style.marginLeft = `${-prevSlide * 100}%`;
        });
        currentSlide = prevSlide;
        updateCarouselBtns();
      }
    });

    next.addEventListener('click', () => {
      if (currentSlide + 1 < totalSlides) {
        const nextSlide = currentSlide + 1;
        firstItems.forEach((firstItem) => {
          firstItem.style.marginLeft = `${-nextSlide * 100}%`;
        });
        currentSlide = nextSlide;
        updateCarouselBtns();
      }
    });
  }

  /**
   * add FAQ behavior
   */
  public faqBehavior() {
    /**
     * @note the markup of the FAQ items ** should ** be converted
     * from a div-based list to a semantically correct ul-li list.
     * A ul-li list will allow attaching event to the parent
     * element. Alt. FAQ items can be wrapped into a container
     * div (or section), which will allow similar event binding.
     */
    const { faqItems } = this.cached;

    faqItems.forEach((faqItem) => {
      const faqQuestion = faqItem.querySelector('.faq-question');
      const faqAnswer = faqItem.querySelector('.faq-answer');
      const expandIcon = faqItem.querySelector('.icon-expand');
      const collapseIcon = faqItem.querySelector('.icon-collapse');

      faqQuestion.addEventListener('click', () => {
        faqAnswer.classList.toggle('hidden');
        expandIcon.classList.toggle('hidden');
        collapseIcon.classList.toggle('hidden');
      });
    });
  }

  /**
   * add Text Me form behavior
   */
  public textMeFormBehavior() {
    const { textMeForms, textMeFormInputs, textMeFormSubmitBtns } = this.cached;

    textMeFormInputs.forEach((textMeFormInput) => {
      textMeFormInput.addEventListener('change', (event) => {
        if (event.target instanceof HTMLInputElement) {
          const textMeInputElement = event.target;
          const phoneVal = phone(event.target.value);
          const textMeFormError = textMeInputElement.nextElementSibling;
          const textMeSubmitBtn =
            textMeInputElement.parentElement.nextElementSibling.querySelector(
              'button'
            );
          if (phoneVal.isValid) {
            textMeFormInput.classList.remove('border-red-500');
            textMeFormError.classList.add('hidden');
            textMeSubmitBtn.classList.remove('opacity-25');
            textMeSubmitBtn.classList.remove('cursor-not-allowed');
            textMeSubmitBtn.removeAttribute('disabled');
          } else {
            textMeFormInput.classList.add('border-red-500');
            textMeFormError.classList.remove('hidden');
            textMeSubmitBtn.classList.add('opacity-25');
            textMeSubmitBtn.classList.add('cursor-not-allowed');
            textMeSubmitBtn.setAttribute('disabled', 'disabled');
          }
        }
      });
    });

    textMeFormSubmitBtns.forEach((textMeFormSubmitBtn) => {
      textMeFormSubmitBtn.addEventListener('click', () => {
        trackButtonPress({
          source_type: 'sms_link',
          source: {
            Phone: phone(
              textMeFormSubmitBtn.parentElement.previousElementSibling.querySelector(
                'input'
              ).value
            ).phoneNumber,
          },
          step_value: 'signup',
        });
      });
    });

    textMeForms.forEach((textMeForm) => {
      textMeForm.addEventListener('submit', () => {
        trackEvent('signup', {
          source_type: 'sms_link_submit',
          source: {
            Phone: phone(
              (textMeForm.querySelector('.text-me-input') as HTMLInputElement)
                .value
            ).phoneNumber,
          },
          step_value: 'signup',
        });
      });
    });
  }

  /**
   * add Download the app behavior
   */
  public updateDownloadAppUrl() {
    if ((window as any).use_branch_download_link) {
      return;
    }
    const { navMenuItemDownloadApp } = this.cached;
    const { currentHost } = this.gon;
    let downloadAppUrl = `${currentHost}/apps`
    if (navigator.userAgent.match(/Android/i)) {
      downloadAppUrl =
        'https://play.google.com/store/apps/details?id=com.instacart.shopper';
    } else if (navigator.userAgent.match(/iPhone/i)) {
      downloadAppUrl = 'https://itunes.apple.com/us/app/instacart-shopper/id1454056744';
    }
    navMenuItemDownloadApp.href = downloadAppUrl;
  }
}
